// The Overall-health meter's grade and its change readout: which earlier
// reading the live score is measured against (24 hours ago), how that reading
// is named, and whether the grade moved since.
//
// Split out of Outlook.tsx so the derivation is testable without React —
// same pattern as daySummary.ts. Everything here is pure.

/** One letter band and its floor, best first. */
const GRADE_BANDS = [
  { letter: 'A', cls: 'g-a', lo: 90 },
  { letter: 'B', cls: 'g-b', lo: 80 },
  { letter: 'C', cls: 'g-c', lo: 60 },
  { letter: 'D', cls: 'g-d', lo: 50 },
] as const

export interface HealthGrade {
  /** What the tag prints: the bare letter — "A", "B", "C", "D", "F". No −/+
   *  modifiers. */
  grade: string
  /** Colour class, one per letter. */
  cls: string
  /** Order on the scale, F = 0 … A = 4, so two grades can be compared
   *  without parsing the string. */
  rank: number
}

/**
 * Letter grade for a 0..100 score: A ≥90 · B ≥80 · C ≥60 · D ≥50 · F below.
 * Letters only — no −/+ steps.
 *
 * The backend's `environment_confidence` tool (`_grade`) prints the same
 * grade to the assistant — change both or neither.
 */
export function healthGrade(p: number): HealthGrade {
  for (let i = 0; i < GRADE_BANDS.length; i++) {
    const b = GRADE_BANDS[i]
    // Bands are listed best-first, so the rank counts down to F's 0.
    if (p >= b.lo) return { grade: b.letter, cls: b.cls, rank: GRADE_BANDS.length - i }
  }
  return { grade: 'F', cls: 'g-f', rank: 0 }
}

/** One compiled daily row as the meter needs it. `ConfidenceDay` — the
 *  `/confidence/history` shape — satisfies it structurally, so the meter and
 *  the 7-day strip read the same rows and can never name two numbers for one
 *  day. `day` is the ISO date the rollup compiled. */
export interface HealthHistoryRow {
  day: string
  score: number | null
}

/** The reading 24 hours ago — `day_ago` on the `/confidence` payload: the
 *  hourly snapshot nearest to that mark, and when it was captured. */
export interface HealthDayAgo {
  /** 0..1 */
  score: number
  /** ISO timestamp of the snapshot. */
  at: string
}

/** The reading the live score is compared against. */
export interface HealthBaseline {
  /** 0..100, already rounded the way the card prints a score. */
  pct: number
  /** Its letter grade — the readout compares grades ("from A 24hr ago"). */
  grade: string
  /** How the sentence names the reading, preposition included, so the card can
   *  print `from ${grade} ${when}` without knowing which it got:
   *  "24hr ago" · "yesterday" · "on Sep 9". */
  when: string
  /** The same name for "same as ${name}":
   *  "last 24hr" · "yesterday" · "Sep 9". */
  name: string
  kind: 'day_ago' | 'yesterday' | 'day'
  /** How the tooltips place the reading in time, after its N/100:
   *  "24hr ago" · "at the close of Tue Sep 9". */
  moment: string
}

// Same day arithmetic as buildWeek: ISO keys in UTC, labels in the browser's
// locale, so the meter and the strip agree on which row is which day.
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10)
}
function shifted(d: Date, days: number): Date {
  const out = new Date(d)
  out.setDate(d.getDate() + days)
  return out
}
function labels(iso: string): { monthDay: string; weekday: string } {
  // Noon, local: immune to the UTC/DST shifts that would move midnight to the
  // day before.
  const d = new Date(`${iso}T12:00:00`)
  return {
    monthDay: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
  }
}

const HOUR_MS = 3_600_000
// How far from the 24h mark a reading may sit and still be called "24hr ago".
// The backend already picks within two hours of it; the extra hour covers the
// payload's 10-minute cache. What this really stops is a payload painted from
// the widget's local cache, whose `day_ago` can be days old until the fresh
// one lands.
const DAY_AGO_SLACK_MS = 3 * HOUR_MS

/**
 * The baseline the meter compares against: the reading 24 HOURS AGO ("from A
 * 24hr ago" / "same as last 24hr"), or null when there is nothing to compare
 * with (a brand-new client, whose card then says nothing rather than inventing
 * a comparison).
 *
 * `dayAgo` is `day_ago` from the `/confidence` payload. When it is missing —
 * no snapshot landed near that hour, a pre-upgrade cached payload — or is not
 * actually about 24 hours before `now`, the NEWEST compiled daily close before
 * today stands in and is NAMED ("from B yesterday", "same as Sep 10"), because
 * "last 24hr" printed over a three-day-old row is a quiet lie.
 *
 * `history` is what `/confidence/history` returns. Today's own row, if the
 * rollup has already run, is never its own baseline.
 */
export function healthBaseline(
  dayAgo: HealthDayAgo | null | undefined,
  history: HealthHistoryRow[],
  now: Date = new Date(),
): HealthBaseline | null {
  if (dayAgo) {
    const age = now.getTime() - new Date(dayAgo.at).getTime()
    if (Math.abs(age - 24 * HOUR_MS) <= DAY_AGO_SLACK_MS) {
      const pct = Math.round(dayAgo.score * 100)
      return { pct, grade: healthGrade(pct).grade, when: '24hr ago', name: 'last 24hr', kind: 'day_ago', moment: '24hr ago' }
    }
  }
  const todayIso = isoDay(now)
  const yesterdayIso = isoDay(shifted(now, -1))
  const compiled = history
    .filter((r): r is HealthHistoryRow & { score: number } => r.score != null && r.day < todayIso)
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))
  if (compiled.length === 0) return null
  const row = compiled[compiled.length - 1]
  const kind: HealthBaseline['kind'] = row.day === yesterdayIso ? 'yesterday' : 'day'
  const { monthDay, weekday } = labels(row.day)
  const pct = Math.round(row.score * 100)
  return {
    pct,
    grade: healthGrade(pct).grade,
    when: kind === 'yesterday' ? 'yesterday' : `on ${monthDay}`,
    name: kind === 'yesterday' ? 'yesterday' : monthDay,
    kind,
    moment: `at the close of ${weekday} ${monthDay}`,
  }
}

/** What the readout beside the grade tag says. */
export interface HealthChange {
  /** Which arrow: rise, fall, or the level dash. */
  dir: -1 | 0 | 1
  /** "from A 24hr ago" · "same as last 24hr". */
  text: string
}

/**
 * The change readout, or null with no baseline.
 *
 * It compares GRADES, not points, because grades are what it prints: the
 * arrow, the words and the tag beside them must never disagree. A different
 * grade reads "from B 24hr ago" under a rise or a fall; the same grade reads
 * "same as last 24hr" under the dash — never "from B" beside a live B. The
 * grade is monotonic in the score, so the arrow cannot point against the
 * numbers either; a move too small to change the grade stays in the tooltip,
 * which carries both N/100 figures.
 *
 * Graded on the PRINTED integer, the way the tag is, so the two can never
 * round apart.
 */
export function healthChange(score: number, prev: HealthBaseline | null): HealthChange | null {
  if (!prev) return null
  const moved = healthGrade(Math.round(score * 100)).rank - healthGrade(prev.pct).rank
  if (moved === 0) return { dir: 0, text: `same as ${prev.name}` }
  return { dir: moved > 0 ? 1 : -1, text: `from ${prev.grade} ${prev.when}` }
}
